import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ConnectedPosition } from '@angular/cdk/overlay';
import {
  ATTACHMENTS_TYPES,
  CHANNEL_STATUS,
  CHANNEL_TYPE,
  CHAT_EXTERNAL_TYPE,
  CHAT_SUB_TYPES,
  DEFAULT_ERROR,
  entityIcons,
  ERROR_MESSAGES,
  EXTERNAL_ID_PREFIX,
  activeTranslations,
  MESSAGE_TYPES,
  SEND_ACTION_TYPE,
  UPLOAD_FILE_ICONS,
  UPLOAD_FILE_TYPES,
  VIBER_SOCKET_ERROR,
} from '../../../../../enum/constants';
import { isCurrentWeek, isToday, isYesterday } from '../../../../../helpers';
import { ChatManager, ChatService } from '../../../../../services/chat.service';
import { BaseService } from '../../../../../services/base.service';
import { NotificationService } from '../../../../../services/notifications.service';
import { FormControl } from '@angular/forms';
import { debounceTime, filter, finalize, map, Subject, take, takeUntil } from 'rxjs';
import {
  FORMAT_BY_CHAT_TYPE,
  IMenuButton,
  ISendMessageBody,
  MESSAGES_INFO_POSITION,
  OLD_RIA_CHAT_TYPES,
  TMessageAction,
  UPLOAD_FILES_DISABLED_CHAT_TYPES,
  UPLOAD_FILES_DISABLED_SEND_ACTIONS,
  UploadAttachments,
  VOICE_MESSAGE_CHAT_TYPES,
} from './chat.interface';
import { ModalService } from '../../../../../services/modal.service';
import { FileUploadNewComponent } from '../../../../../modals/file-upload-new/file-upload-new.component';
import { RIGHT_MENU } from '../../chat-sidebar.interface';
import { CHAT_TABS } from '../chat-list/chat-list.interface';
import { IChat } from '../../../../../interfaces';
import { Attachment, editMessageTypes, Message } from '../chat-message/chat-message.interface';
import { MessageTemplatesComponent } from '../../../../../bottom-sheet/message-templates/message-templates.component';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { DialogService } from '../../../../../services/dialog.service';
import { Router } from '@angular/router';
import { StackedSidebarService } from '../../../../../services/stacked-sidebar.service';
import { TranslateService } from '@ngx-translate/core';
import { capitalizeFirstLetter, getExternalIDPrefixType } from '../../../../../helpers/utils';
import { WebSocketService } from '../../../../../services/gateway.service';
import { PlannerService } from '../../../../../services/planner.service';
import { PlannerItem } from '../../../stacked-sidebar/components/planner-v2/planner-v2.interface';
import { RequestHelper } from '../../../../../services/request-helper.service';
import { IViberSocketError } from '../../../right-sidebar/components/integration-instagram/integration.interface';
import { AiService } from '../../../../../services/ai.service';
import { AI_TASK_TYPE, GenerateAIResponseBody } from '../../../../../interfaces/ai.interface';
import { FileSystemService } from '../../../../../services/file-system.service';
import {
  VoiceRecordingFacade,
  VoiceRecordingResult,
  VoiceRecordingStartOptions,
} from '../../../../../facades/voice-recording.facade';

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @ViewChild('chatInput') chatInput!: ElementRef;
  @ViewChild('messageContainer') messageContainer!: ElementRef;
  @Input() chat!: IChat;
  private readonly destroy$: Subject<void> = new Subject<void>();
  private readonly actionsCancel$: Subject<void> = new Subject<void>();
  protected managers: ChatManager[] = [];
  protected managerSearchTerm = '';
  protected isManagersPickerOpen = false;
  protected managersPickerOrigin!: HTMLElement;
  protected isAssigningManager = false;
  protected readonly managersPickerPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -8 },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8 },
  ];

  private searchTerms = new Subject<string>();
  protected voiceActive = false;
  protected menuButtonsDesktop: IMenuButton[] = [
    { title: 'search', icon: 'search', action: () => this.toggleSearch() },
    { title: 'pinned_messages', icon: 'pinboard', action: () => this.togglePinned() },
    { title: 'delayed_messages', icon: 'calendar_clock', action: () => this.toggleDelayed() },
    { title: 'close_chat', icon: 'archive', action: () => this.moveChat(CHAT_TABS.CLOSED) },
    { title: 'open_a_chat', icon: 'unarchive', action: () => this.moveChat(CHAT_TABS.ACTIVE) },
    { title: 'about_chat', icon: 'info', action: () => this.openAbout() },
  ];
  protected menuButtons: IMenuButton[] = [
    { title: 'close_chat', icon: 'archive', action: () => this.moveChat(CHAT_TABS.CLOSED) },
    { title: 'open_a_chat', icon: 'unarchive', action: () => this.moveChat(CHAT_TABS.ACTIVE) },
    { title: 'about_chat', icon: 'info', action: () => this.openAbout(), divider: true },
    { title: 'search', icon: 'search', action: () => this.toggleSearch() },
    { title: 'pinned_messages', icon: 'pinboard', action: () => this.togglePinned() },
    { title: 'delayed_messages', icon: 'calendar_clock', action: () => this.toggleDelayed() },
  ];
  protected chatMenuButtons: IMenuButton[] = [
    { title: 'fast_reply', icon: 'bolt', action: () => this.openTemplates(), key: 'fast_reply' },
    { title: 'auto_reply', icon: 'robot', is_svg: true, action: () => this.getAiAnswer(), key: 'auto_reply' },
    { title: 'correct_text', icon: 'spellcheck', action: () => this.correctTextAI(), key: 'correct_text' },
    {
      title: 'file',
      icon: 'description',
      action: () => this.openFileUpload(),
      key: 'upload',
      disabled: () => this.isFileMenuButtonDisabled(),
    },
  ];

  protected lastClickAction!: IMenuButton;
  protected textControl!: FormControl;
  protected messageAction: TMessageAction = {
    action: SEND_ACTION_TYPE.SEND,
    actionText: '',
    isMyActionMessage: false,
    actionXData: {},
  };
  protected attachments: UploadAttachments[] = [];
  protected isResultContainer: boolean = false;
  protected isDragging = false;
  protected isFilesModalShown = false;
  protected loaders = {
    send_voice: false,
  };

  constructor(
    private readonly renderer: Renderer2,
    private readonly notificationService: NotificationService,
    private readonly modalService: ModalService,
    private readonly matBottomSheet: MatBottomSheet,
    private readonly dialogService: DialogService,
    private readonly router: Router,
    private readonly stackedSidebarService: StackedSidebarService,
    private readonly translate: TranslateService,
    private readonly webSocketService: WebSocketService,
    private readonly plannerService: PlannerService,
    private readonly requestHelper: RequestHelper,
    private readonly aiService: AiService,
    private readonly fileSystemService: FileSystemService,
    protected readonly voiceFacade: VoiceRecordingFacade,
    protected readonly chatService: ChatService,
    protected readonly baseService: BaseService,
  ) {
    this.textControl = new FormControl('');
  }

  public ngOnInit() {
    this.initSockets();
    this.initSearch();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes['chat']) {
      this.initChat();
      this.scheduleFocusInput();
    }
  }

  public ngOnDestroy() {
    this.chatService.selectedChatId = '';
    this.chatService.resetChat();
    this.destroy$.next();
    this.destroy$.complete();
    this.actionsCancel$.next();
    this.actionsCancel$.complete();
  }

  public ngAfterViewInit(): void {
    this.scheduleFocusInput();
  }

  private scheduleFocusInput(): void {
    setTimeout(() => this.focusInput());
  }

  private focusInput(): void {
    if (this.baseService.isMobile) return;
    const el = this.chatInput?.nativeElement as HTMLTextAreaElement | undefined;
    el?.focus();
  }

  private initLastAction(): void {
    const lastAction = this.chatMenuButtons.find((v) => v.key === 'fast_reply');
    if (lastAction) this.lastClickAction = lastAction;
    if (localStorage.getItem('lastActionChat')) {
      const lastAction = this.chatMenuButtons.find((v) => v.key === localStorage.getItem('lastActionChat'));
      if (lastAction && !lastAction?.disabled?.()) this.lastClickAction = lastAction;
    }
  }

  private initChat() {
    this.resetInputContainer();
  }

  private initSockets() {
    this.webSocketService
      .messageUpdated()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.chatService.messageUpdatedSocket(data);
        },
        error: () => {},
      });

    this.webSocketService
      .viberError()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.handleViberSocketError(data);
        },
        error: () => {},
      });
  }

  private initSearch(): void {
    this.searchTerms.pipe(takeUntil(this.destroy$), debounceTime(300)).subscribe((text: string) => {
      if (!text.trim()) return;
      this.chatService.resetChatSearchInfo();
      this.chatService.chatSearchInfo.search_text = text;
      this.chatService.searchChatMessages();
    });
  }

  protected onSearchScroll(event: any): void {
    const searchInfo = this.chatService.chatSearchInfo;
    if (searchInfo.loading) return;
    if (event.target.scrollHeight - event.target.scrollTop <= event.target.clientHeight + 400) {
      const candidatePage = searchInfo.page_number + 1;
      if (candidatePage <= searchInfo.page_count) {
        searchInfo.page_number = candidatePage;
        this.chatService.searchChatMessages(true);
      }
    }
  }

  protected toggleSearch() {
    const chatSearchInfo = this.chatService.chatSearchInfo;
    if (!chatSearchInfo.search_text) {
      chatSearchInfo.search_visible = !chatSearchInfo.search_visible;
    }
  }

  protected togglePinned() {
    const chatPinnedInfo = this.chatService.chatPinnedInfo;
    chatPinnedInfo.visible = !chatPinnedInfo.visible;
  }

  protected toggleDelayed() {
    const chatDelayedInfo = this.chatService.chatDelayedInfo;
    chatDelayedInfo.visible = !chatDelayedInfo.visible;

    this.resetInputContainer();
  }

  private openAbout() {
    this.chatService.actionRightMenu(RIGHT_MENU.ABOUT);
  }

  protected sendMessage(chat_id: string) {
    const hadFocus = this.commitComposition();
    const text = this.textControl.value.trim();
    if (!text && !this.attachments.length) {
      if (hadFocus) this.restoreInputFocus();
      return;
    }

    this.sendMessageText(chat_id, text, { array: this.attachments as Attachment[] });

    this.resetInputContainer();
    if (hadFocus) this.restoreInputFocus();
  }

  /**
   * Android keyboards (Gboard text shortcuts, autocorrect) keep the inserted text as an open
   * composition. Changing the textarea value under it desyncs the IME: the old text comes back,
   * typing stops working and Chrome only shows its "revert / delete" suggestion menu.
   * Blurring makes the keyboard commit the composition first. Returns whether the input had focus.
   */
  private commitComposition(): boolean {
    const el = this.chatInput?.nativeElement as HTMLTextAreaElement | undefined;
    if (!el || document.activeElement !== el) return false;

    el.blur();
    return true;
  }

  private restoreInputFocus(): void {
    (this.chatInput?.nativeElement as HTMLTextAreaElement | undefined)?.focus({ preventScroll: true });
  }

  protected resetInputContainer() {
    this.actionsCancel$.next();

    const hadFocus = this.commitComposition();
    this.textControl.reset('');
    this.attachments = [];
    this.messageAction = {
      action: SEND_ACTION_TYPE.SEND,
      actionText: '',
      isMyActionMessage: false,
      actionXData: {},
    };

    if (this.chatInput?.nativeElement) {
      this.chatInput.nativeElement.dispatchEvent(new Event('input'));
    }

    this.initLastAction();
    if (hadFocus) this.restoreInputFocus();
    this.focusInput();
    this.cancelVoiceNow();
  }

  private editDelayedMessage(text: string, delayedMessage?: PlannerItem): boolean {
    const isEdit = this.messageAction.action === SEND_ACTION_TYPE.EDIT;

    if (isEdit && delayedMessage) {
      (delayedMessage.payload as any).automation.x_data.reply = text;
      this.requestHelper.handle<void>(
        this.plannerService.updatePlannerPayload(delayedMessage.id, delayedMessage.payload),
        () => {},
        {
          key: `update_planner_payload_${delayedMessage.id}`,
          destroy$: this.destroy$,
          defaultErrorMessage: 'notifications_texts.error.failed_to_update',
          onFinalize: () => {
            this.chatService.getDelayedByChat();
          },
        },
      );

      return true;
    } else {
      return false;
    }
  }

  private sendMessageText(chatId: string, text: string, attachments: { array: Attachment[] }) {
    const isEdit = this.messageAction.action === SEND_ACTION_TYPE.EDIT;

    if (this.editDelayedMessage(text, this.messageAction.actionXData.delayed)) {
      return;
    }

    const body: ISendMessageBody = {
      text,
      send_action: this.messageAction.action,
      x_data: this.getActionXData(),
    };
    if (this.messageAction.action === SEND_ACTION_TYPE.REPLY_COMMENT) {
      body.text_message = text;
    }

    if (attachments?.array?.length) {
      body['attachments'] = attachments;
    }

    if (
      body.attachments &&
      body.text &&
      [CHAT_EXTERNAL_TYPE.INSTAGRAM, CHAT_EXTERNAL_TYPE.VIBER, CHAT_EXTERNAL_TYPE.PROM].includes(
        this.chat.external_type,
      )
    ) {
      this.notificationService.showError(
        '',
        this.translate.instant('errors.can_send_only_media_without_description', {
          chat_external_type: capitalizeFirstLetter(this.chat.external_type),
        }),
      );
      return;
    }

    this.chatService
      .handleMessageAction(chatId, body)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: Message) => {
          if (isEdit) {
            this.chatService.waitEditMessage(res.id, text);
          } else {
            if (this.messageAction.action === SEND_ACTION_TYPE.REPLY_COMMENT) {
              res.x_data = res.x_data || {};
              if (!res.x_data.reply_to) {
                const replyTo = this.messageAction.actionXData?.reply_to;
                res.x_data.reply_to = replyTo ? { ...replyTo } : { to_msg_text: this.messageAction.actionText };
              }
              if (res.x_data.reply_to && !res.x_data.reply_to.to_msg_text) {
                res.x_data.reply_to.to_msg_text = this.messageAction.actionText;
              }
            }
            this.chatService.addTempMessage(res);
            this.chatService.reloadChatList();
          }
        },
      });
  }

  protected onMouseDown(event: MouseEvent) {
    event.preventDefault();

    const initialHeight = this.chatInput.nativeElement.offsetHeight;
    const initialY = event.clientY;

    const mouseMoveListener = this.renderer.listen('document', 'mousemove', (e) =>
      this.onMouseMove(e, initialHeight, initialY),
    );
    const mouseUpListener = this.renderer.listen('document', 'mouseup', () => {
      mouseMoveListener();
      mouseUpListener();
    });
  }

  protected onMouseMove(event: MouseEvent, initialHeight: number, initialY: number) {
    const deltaY = initialY - event.clientY;
    const newHeight = initialHeight + deltaY;

    if (newHeight > 500) return;

    this.renderer.setStyle(this.chatInput.nativeElement, 'height', 0);
    this.renderer.setStyle(this.chatInput.nativeElement, 'minHeight', `max(48px, min(${newHeight}px, 200px))`);
  }

  /**
   * OS text replacement, autocorrect and IME input run as a composition; the key that confirms it
   * (usually Enter) must reach the browser untouched, otherwise the composition never ends and the
   * textarea gets stuck in the system "Delete / Revert" state.
   */
  private isComposing(e: Event): boolean {
    return (e as KeyboardEvent).isComposing || (e as KeyboardEvent).keyCode === 229;
  }

  public pressCmdEnter(e: Event): void {
    if (this.isComposing(e)) return;

    if (
      this.baseService.currentUserInfo.settings.hot_key_send_chat === 'cmd_enter' &&
      !this.baseService.isMobile &&
      !this.chat.is_blocked
    ) {
      this.sendMessage(this.chat.id);
    }
  }

  public pressShiftEnter(e: Event): void {
    if (this.isComposing(e)) return;

    if (
      this.baseService.currentUserInfo.settings.hot_key_send_chat === 'shift_enter' &&
      !this.baseService.isMobile &&
      !this.chat.is_blocked
    ) {
      e.preventDefault();
      this.sendMessage(this.chat.id);
    }
  }

  protected pressArrowUp(e: Event) {
    if (
      this.isComposing(e) ||
      !editMessageTypes.includes(this.chatService.chat.external_type) ||
      this.textControl.value ||
      this.attachments?.length ||
      this.messageAction.action !== SEND_ACTION_TYPE.SEND
    ) {
      return;
    }

    const firstMessage = this.chatService.chatMessagesInfo.items.find((v) => {
      return this.chatService.canEditMessage(v);
    });

    if (firstMessage) {
      this.onEditMessage(firstMessage);
    }
  }

  protected pressEscape(e: Event) {
    if (this.isComposing(e)) return;

    if (this.messageAction.action !== SEND_ACTION_TYPE.SEND) {
      this.resetInputContainer();
    }
  }

  public pressEnter(e: Event): void {
    if (this.isComposing(e)) return;

    if (
      this.baseService.currentUserInfo.settings.hot_key_send_chat === 'enter' &&
      !this.baseService.isMobile &&
      !this.chat.is_blocked
    ) {
      e.preventDefault();
      this.sendMessage(this.chat.id);
    }
  }

  protected openFileUpload(files?: File[]) {
    if (this.isNotShowOpenFileUpload()) {
      this.notificationService.showError(
        '',
        activeTranslations.notifications_texts.error.unable_to_transfer_files,
      );
      return;
    }

    const uploadFileType = UPLOAD_FILE_TYPES[this.chat.external_type];
    const maxUploadFiles = uploadFileType?.max_upload_files;

    if (maxUploadFiles && this.attachments.length >= maxUploadFiles) {
      this.notificationService.showWarning(
        this.translate.instant('notifications_texts.warn.warn'),
        this.translate.instant('notifications_texts.warn.no_more_upload_files', { limit: maxUploadFiles }),
      );
      return;
    }

    this.isFilesModalShown = true;

    this.modalService
      .openModal({
        translate: true,
        component: FileUploadNewComponent,
        title: 'upload_file',
        height: 'auto',
        data: {
          channel_type: this.chat?.channels?.[0]?.type,
          files,
          length: this.attachments.length,
          outside_files: this.attachments,
          max_upload_files: maxUploadFiles,
          allowed_media_types: uploadFileType?.allowed_media_types,
          max_upload_size: uploadFileType?.max_upload_size,
          is_file_choice: uploadFileType?.is_file_choice,
          solo_media_types: uploadFileType?.solo_media_types,
        },
      })
      .subscribe((res: any[]) => {
        this.isFilesModalShown = false;

        if (res) {
          this.attachments.push(
            ...res.map((v) => ({
              url: v.link,
              key: v.key,
              mimeType: v.mimeType,
              formattedType: v.formattedType,
              type: v.type,
              name: v.name,
              size: v.size,
            })),
          );
        }
      });
  }

  protected onEditMessage(message: Message) {
    const text = message.text || '';

    this.messageAction.action = SEND_ACTION_TYPE.EDIT;
    const hadFocus = this.commitComposition();
    this.textControl.setValue(text);
    if (hadFocus) this.restoreInputFocus();
    this.messageAction.actionText = text;
    this.messageAction.actionXData = {
      message_id: message.id,
    };

    if (message.x_data?.planner) {
      this.messageAction.actionXData.delayed = message.x_data.planner;
    }

    this.attachments = [];
    if (this.chatInput?.nativeElement) {
      this.chatInput.nativeElement.dispatchEvent(new Event('input'));
    }

    this.scheduleFocusInput();
  }

  protected onReply(event: any) {
    this.messageAction.action = event.send_action ? event.send_action : SEND_ACTION_TYPE.SEND;
    this.messageAction.actionText = this.getReplyActionText(event);
    if (this.messageAction.action === SEND_ACTION_TYPE.REPLY_COMMENT && !this.messageAction.actionText) {
      this.messageAction.actionText =
        event.message?.x_data?.value?.text || event.message?.x_data?.text || event.message?.text || '';
    }
    this.messageAction.isMyActionMessage = !!event.message.created_by;

    this.messageAction.actionXData = {
      reply_to: {
        to_msg_id: event.message.id,
        to_name: this.getActionDescription(),
      },
      x_data: event.message.x_data,
    };
    if (this.messageAction.action === SEND_ACTION_TYPE.REPLY_COMMENT) {
      const senderName = event.message?.x_data?.sender?.name;
      if (senderName && this.messageAction.actionXData.reply_to) {
        this.messageAction.actionXData.reply_to.to_name = senderName;
      }
    }

    this.initLastAction();
    this.scheduleFocusInput();
  }

  private getReplyActionText(event: any): string {
    switch (getExternalIDPrefixType(event.message.external_id)) {
      case EXTERNAL_ID_PREFIX.OLX_UA: {
        return '';
      }

      case EXTERNAL_ID_PREFIX.WHATSAPP:
      case EXTERNAL_ID_PREFIX.INSTAGRAM_COMMENT:
      case EXTERNAL_ID_PREFIX.FACEBOOK_COMMENT:
      case EXTERNAL_ID_PREFIX.TG_USER: {
        return this.parseReplyActionText(event);
      }
    }

    return '';
  }

  private parseReplyActionText(event: any): string {
    if (event.message.text) {
      return event.message.text;
    }
    if (event.message.attachments) {
      return this.translate.instant('media_files');
    }

    return '';
  }

  protected scrollTo(event: any) {
    const scrollDistancePx = event.target.scrollHeight * 0.2;
    const scrollTop = Math.abs(event.target.scrollTop);
    if (scrollTop + event.target.clientHeight >= event.target.scrollHeight - scrollDistancePx) {
      this.loadDownChats(event);
    }
    if (scrollTop <= scrollDistancePx) {
      this.loadUpChats(event);
    }
  }

  protected scrollBottom(messageId?: string) {
    if (!messageId) return;

    this.chatService.findMessage(messageId, { behavior: 'smooth', is_highlighted: false });
  }

  protected isBtnBottomShow(): boolean {
    return (
      Math.abs(this.messageContainer?.nativeElement.scrollTop) > 300 ||
      !this.chatService.hasMessagesInfoItem(MESSAGES_INFO_POSITION.FIRST)
    );
  }

  protected loadUpChats(event?: any) {
    if (this.chatService.isLoaderChat.down || this.chatService.isLoaderChat.up) return;

    if (!this.chatService.hasMessagesInfoItem(MESSAGES_INFO_POSITION.FIRST)) {
      this.chatService.loadChatMessages(true, { direction: 'up', event }, true);
    }
  }

  protected loadDownChats(event?: any) {
    if (this.chatService.isLoaderChat.down || this.chatService.isLoaderChat.up) return;

    if (!this.chatService.hasMessagesInfoItem(MESSAGES_INFO_POSITION.LAST)) {
      this.chatService.loadChatMessages(true, { direction: 'down', event }, true);
    }
  }

  protected onResendMessage(event: { text: string; attachments: { array: Attachment[] } }) {
    return this.sendMessageText(this.chat.id, event.text, event.attachments);
  }

  protected onSearch(text: string) {
    if (!text.trim()) this.chatService.resetChatSearchInfo();
    this.searchTerms.next(text);
  }

  protected onSearchFocusIn() {
    this.isResultContainer = true;
  }

  protected onSearchFocusOut() {
    this.isResultContainer = false;
  }

  protected addEmoji(event: any) {
    const emoji = event.emoji.native;
    const inputElement = this.chatInput.nativeElement;
    if (!inputElement) {
      return;
    }
    this.commitComposition();
    const start = inputElement.selectionStart;
    const end = inputElement.selectionEnd;
    const text = this.textControl.value;

    const newText = text.substring(0, start) + emoji + text.substring(end);

    this.textControl.setValue(newText);

    setTimeout(() => {
      this.focusInput();
      inputElement.setSelectionRange(start + emoji.length, start + emoji.length);
    }, 0);
  }

  protected selectSearchMessage(message_id: string, index: number) {
    this.chatService.chatSearchInfo.selected_number = index;
    const searchInput: HTMLInputElement | null = document.querySelector('.chat-header app-search input');
    if (searchInput) {
      searchInput.blur();
    }
    this.chatService.findMessage(message_id);
  }

  protected arrowSearch(direction: 'up' | 'down') {
    const chatSearchInfo = this.chatService.chatSearchInfo;
    if (!chatSearchInfo.items) return;

    let number = 0;
    if (chatSearchInfo.selected_number || chatSearchInfo.selected_number === 0) {
      if (direction === 'up' && chatSearchInfo.selected_number + 1 < chatSearchInfo.items.length) {
        chatSearchInfo.selected_number = chatSearchInfo.selected_number + 1;
      } else if (direction === 'down' && chatSearchInfo.selected_number - 1 >= 0) {
        chatSearchInfo.selected_number = chatSearchInfo.selected_number - 1;
      }
      number = chatSearchInfo.selected_number;
    } else chatSearchInfo.selected_number = number;

    const message_id = chatSearchInfo.items[number].id;
    this.chatService.findMessage(message_id);
  }

  protected deleteFile(index: number) {
    this.attachments.splice(index, 1);
  }

  protected isMenuButtonVisible(item: IMenuButton): boolean {
    let condition = true;
    if (item.title === 'pinned_messages') condition = !!this.chatService.chatPinnedInfo.items.length;
    if (item.title === 'delayed_messages') condition = !!this.chatService.chatDelayedInfo.items.length;
    if (item.title === 'search') condition = !OLD_RIA_CHAT_TYPES.includes(this.chat.external_type);
    if (item.title === 'close_chat') condition = this.chat.tab_status === CHAT_TABS.ACTIVE;
    if (item.title === 'open_a_chat') condition = this.chat.tab_status === CHAT_TABS.CLOSED;
    return condition;
  }

  private moveChat(tabStatus: CHAT_TABS) {
    this.chatService.onMoveChat(this.chat.id, tabStatus);
  }

  private openTemplates() {
    const bottomRef = this.matBottomSheet.open(MessageTemplatesComponent, { hasBackdrop: true });
    bottomRef
      .afterDismissed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res) {
          this.commitComposition();
          this.textControl.setValue(this.textControl.value ? this.textControl.value + `\n${res.text}` : res.text);
          this.chatInput.nativeElement.dispatchEvent(new Event('input'));
          this.scheduleFocusInput();
        }
      });
  }

  private checkIfHasAI() {
    const isAi = this.baseService.currentCompanyInfo.application_keys.includes('GEN_AI');

    if (!isAi) {
      const dialogRef = this.dialogService.openConfirm({
        text: 'buy_ai',
      });
      dialogRef.subscribe((res) => {
        if (res) {
          this.router.navigate(['/crm/marketplace']);
          this.stackedSidebarService.closeChats();
        }
      });

      return false;
    }

    return true;
  }

  private getAiAnswer() {
    if (!this.checkIfHasAI()) return;

    const payload: GenerateAIResponseBody = {
      task: AI_TASK_TYPE.CHAT_REPLY,
      x_data: {
        chat_id: this.chatService.chat.id,
      },
    };

    this.requestHelper.handle<string>(
      this.aiService.generateAIResponse(payload),
      (text: string) => {
        if (!text?.trim()) return;

        this.animateTextInput(text);
      },
      {
        key: 'chat_get_ai_answer',
        destroy$: this.actionsCancel$,
        loaderRef: this.chatService.isLoaderChat,
        loaderKey: 'input_actions',
        defaultErrorMessage: 'notifications_texts.error.failed_to_generate_ai_answer',
      },
    );
  }

  private correctTextAI() {
    const text: string = this.textControl.value?.trim();

    if (!text || !this.checkIfHasAI()) return;

    const payload: GenerateAIResponseBody = {
      task: AI_TASK_TYPE.CORRECT_TEXT,
      x_data: {
        text,
      },
    };

    this.requestHelper.handle<any>(
      this.aiService.generateAIResponse(payload),
      (text: string) => {
        if (!text?.trim()) return;

        this.animateTextInput(text);
      },
      {
        key: 'chat_correct_text_ai',
        destroy$: this.actionsCancel$,
        loaderRef: this.chatService.isLoaderChat,
        loaderKey: 'input_actions',
        defaultErrorMessage: 'notifications_texts.error.failed_to_correct_text_ai',
      },
    );
  }

  private animateTextInput(text: string) {
    this.textControl.setValue(text);
    this.chatInput.nativeElement.dispatchEvent(new Event('input'));
    this.scheduleFocusInput();

    // this.resetInputContainer();
    // let index = 0;
    // const interval = setInterval(() => {
    //   if (index < text.length) {
    //     element.value += text[index];
    //     element.dispatchEvent(new Event('input'));
    //     index++;
    //   } else {
    //     clearInterval(interval);
    //     this.textControl.setValue(text);
    //   }
    // }, 15);
  }

  protected saveLastItem(item: IMenuButton) {
    this.lastClickAction = item;
    if (item.key) localStorage.setItem('lastActionChat', item.key);
    return item;
  }

  protected getActionIcon(): string | null {
    switch (this.messageAction.action) {
      case SEND_ACTION_TYPE.REPLY:
      case SEND_ACTION_TYPE.SEND_MESSAGE_BY_COMMENT_TO_DIRECT:
      case SEND_ACTION_TYPE.REPLY_COMMENT: {
        return 'reply';
      }

      case SEND_ACTION_TYPE.EDIT: {
        return 'edit';
      }

      default: {
        return null;
      }
    }
  }

  protected getActionDescription(): string {
    switch (this.messageAction.action) {
      case SEND_ACTION_TYPE.EDIT: {
        return this.translate.instant('edit_message');
      }

      case SEND_ACTION_TYPE.REPLY: {
        return this.translate.instant('reply_to', { actionDescRecipient: this.getActionDescriptionRecipient() });
      }

      case SEND_ACTION_TYPE.REPLY_COMMENT: {
        return this.translate.instant('reply_comment_to', {
          actionDescRecipient: this.getActionDescriptionRecipient(),
        });
      }

      case SEND_ACTION_TYPE.SEND_MESSAGE_BY_COMMENT_TO_DIRECT: {
        return this.translate.instant('reply_to_direct_to', {
          actionDescRecipient: this.getActionDescriptionRecipient(),
        });
      }

      default: {
        return '';
      }
    }
  }

  private getActionDescriptionRecipient(): string {
    if (this.messageAction.isMyActionMessage) return this.translate.instant('to_myself');
    return this.chat.title;
  }

  private getActionXData() {
    const x_data = this.messageAction.actionXData;
    switch (this.messageAction.action) {
      case SEND_ACTION_TYPE.REPLY_COMMENT: {
        const rawXData = x_data?.x_data;
        const isTiktokComment = !!(rawXData?.tiktok?.comment_id || rawXData?.post?.video_id);
        const commentId = rawXData?.tiktok?.comment_id;
        const videoId = rawXData?.tiktok?.video_id || rawXData?.post?.video_id;
        const replyTo = x_data?.reply_to ? { ...x_data.reply_to } : undefined;
        if (replyTo && !replyTo.to_msg_text) {
          replyTo.to_msg_text = this.messageAction.actionText;
        }

        if (isTiktokComment) {
          const payload: Record<string, any> = {
            tiktok: {
              comment_id: commentId,
              video_id: videoId,
            },
          };
          if (replyTo) {
            payload['reply_to'] = replyTo;
          }
          return payload;
        }

        if (rawXData && replyTo) {
          return { ...rawXData, reply_to: replyTo };
        }

        return rawXData || x_data;
      }

      case SEND_ACTION_TYPE.SEND_MESSAGE_BY_COMMENT_TO_DIRECT:
      case SEND_ACTION_TYPE.REPLY: {
        x_data?.reply_to ? (x_data.reply_to.to_msg_text = this.messageAction.actionText) : x_data;
        // { to_msg_id: this.messageAction.actionXData.message_id,
        //   to_msg_text: this.messageAction.actionText,
        //   to_name: this.messageAction.actionXData.reply_to_name, //TODO add opponent and other names if you need that
        // };
        return x_data;
      }

      default: {
        return x_data;
      }
    }
  }

  protected hasConnectedChannel(): boolean {
    return (this.chat.channels ?? []).some((channel) => channel.status === CHANNEL_STATUS.CONNECTED);
  }

  protected isCompanyPaused(): boolean {
    return !!this.baseService.company?.is_paused;
  }

  protected isShowInputContainer(): boolean {
    return this.canShowComposer() && !this.needsManagerAssignment();
  }

  protected isShowUnassignedBlock(): boolean {
    return this.canShowComposer() && this.needsManagerAssignment();
  }

  /** Unassigned chats ask to take/transfer them first, when the company enables it in its settings */
  private needsManagerAssignment(): boolean {
    return this.baseService.company?.settings?.is_chat_manager_assignment === true && !this.chat.assigned_user;
  }

  private canShowComposer(): boolean {
    return (
      !this.isCompanyPaused() &&
      this.hasConnectedChannel() &&
      !(
        this.isCommentChat() &&
        ![SEND_ACTION_TYPE.SEND_MESSAGE_BY_COMMENT_TO_DIRECT, SEND_ACTION_TYPE.REPLY_COMMENT].includes(
          this.messageAction.action,
        )
      ) &&
      !OLD_RIA_CHAT_TYPES.includes(this.chat.external_type) &&
      !this.chatService.chatPinnedInfo.visible &&
      (!this.chatService.chatDelayedInfo.visible || this.messageAction.action === SEND_ACTION_TYPE.EDIT)
    );
  }

  private isNotShowOpenFileUpload(): boolean {
    return (
      UPLOAD_FILES_DISABLED_CHAT_TYPES.includes(this.chat.external_type) ||
      [SEND_ACTION_TYPE.REPLY_COMMENT, SEND_ACTION_TYPE.SEND_MESSAGE_BY_COMMENT_TO_DIRECT].includes(
        this.messageAction.action,
      ) ||
      this.isCommentChat()
    );
  }

  private isCommentChat(): boolean {
    const externalIdPrefix = getExternalIDPrefixType(this.chatService.chat.external_id);
    return (
      externalIdPrefix === EXTERNAL_ID_PREFIX.INSTAGRAM_COMMENT ||
      externalIdPrefix === EXTERNAL_ID_PREFIX.FACEBOOK_COMMENT
    );
  }

  private handleViberSocketError(data: IViberSocketError) {
    switch (data.code) {
      case VIBER_SOCKET_ERROR.CHAT_IS_NOT_AVAILABLE:
      case VIBER_SOCKET_ERROR.VIBER_SEND_MESSAGE_ERROR: {
        if (this.chat.id !== data.chatId) return;
        break;
      }

      case VIBER_SOCKET_ERROR.UNEXPECTED_ERROR: {
        break;
      }
    }

    this.notificationService.showError(
      activeTranslations.notifications_texts.error.error,
      ERROR_MESSAGES[data.code] || DEFAULT_ERROR,
    );
  }

  @HostListener('window:paste', ['$event'])
  eventPaste(e: ClipboardEvent) {
    if (this.isFilesModalShown || OLD_RIA_CHAT_TYPES.includes(this.chat?.external_type) || !e.clipboardData) return;

    const files = Array.from(e.clipboardData.items)
      .map((v: DataTransferItem) => v.getAsFile())
      .filter((v): v is File => !!v);

    if (files?.length) {
      this.openFileUpload(files);
      e.preventDefault();
    }
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    if (event.dataTransfer?.files?.length) {
      const files = Array.from(event.dataTransfer.files);
      this.openFileUpload(files);
    }
  }

  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  @HostListener('dragleave', ['$event'])
  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  private isFileMenuButtonDisabled() {
    return (
      UPLOAD_FILES_DISABLED_SEND_ACTIONS.includes(this.messageAction?.action) ||
      UPLOAD_FILES_DISABLED_CHAT_TYPES.includes(this.chat?.external_type)
    );
  }

  get isVoiceMessageButton(): boolean {
    const noText = !this.textControl.value.trim();
    const noAttachments = !this.attachments.length;
    const isSend = [SEND_ACTION_TYPE.SEND, SEND_ACTION_TYPE.REPLY].includes(this.messageAction.action);
    const isAllowedChatType = this.VOICE_MESSAGE_CHAT_TYPES.includes(this.chat.external_type);

    return noText && noAttachments && isSend && isAllowedChatType;
  }

  protected recordVoiceMessage() {
    if (!this.isVoiceMessageButton || this.voiceActive) return;

    const opts: VoiceRecordingStartOptions = {
      maxDurationSec: 300,
      format: FORMAT_BY_CHAT_TYPE[this.chat.external_type],
    };

    this.voiceFacade
      .startRecordingVoice$(opts)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.voiceActive = true;
        },
        error: (e: string) => {
          this.onVoiceError(e);
        },
      });
  }

  protected sendVoiceNow() {
    if (!this.voiceActive) return;

    this.voiceFacade
      .stopRecordingVoice$()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: VoiceRecordingResult) => {
          this.voiceActive = false;
          this.onVoiceSend(res);
        },
        error: (e: string) => {
          this.voiceActive = false;
          this.onVoiceError(e);
        },
      });
  }

  protected cancelVoiceNow() {
    if (!this.voiceActive) return;

    try {
      this.voiceFacade.cancelRecordingVoice();
    } finally {
      this.voiceActive = false;
      this.onVoiceCancel();
    }
  }

  protected onVoiceSend(result: VoiceRecordingResult) {
    const mime = result.blob.type;
    const ext = mime.split('/')[1];

    const filename = `voice_${Date.now()}.${ext}`;
    const file = new File([result.blob], filename, { type: mime });

    const request$ = this.fileSystemService.uploadTempFile(file, this.chat.channel_type as CHANNEL_TYPE).pipe(
      filter((u: any) => !!u && u.state === true),
      take(1),
      map((ev: any) => ev.body.data),
      map((v: any) => ({
        url: v.link,
        mimeType: v.mimeType,
        formattedType: v.formattedType,
        type: v.type,
        name: v.name,
        size: v.size,
        voice: true,
      })),
    );

    this.requestHelper.handle<any>(
      request$,
      (att: UploadAttachments) => {
        this.attachments.push(att);
        this.sendMessage(this.chat.id);
      },
      {
        key: 'chat_upload_voice',
        destroy$: this.destroy$,
        loaderRef: this.loaders,
        loaderKey: 'send_voice',
        defaultErrorMessage: 'notifications_texts.error.failed_to_update',
      },
    );
  }

  protected onVoiceCancel() {
    this.resetInputContainer();
  }

  protected onVoiceError(code: string) {
    this.notificationService.showError(
      this.translate.instant('notifications_texts.error.error'),
      this.translate.instant('notifications_texts.error.' + code),
    );
    this.onVoiceCancel();
  }

  protected takeChat(): void {
    this.assignChatManager(this.baseService.user.id);
  }

  protected toggleManagersPicker(event: MouseEvent): void {
    event.stopPropagation();
    const target = event.target as HTMLElement;
    this.managersPickerOrigin = target.closest('button') || target;
    this.managerSearchTerm = '';
    this.isManagersPickerOpen = !this.isManagersPickerOpen;
    this.managers = [];
    if (!this.isManagersPickerOpen) return;
    const chatId = this.chat.id;
    this.chatService
      .getAssignableManagers(chatId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((managers) => {
        if (this.chat.id === chatId && this.isManagersPickerOpen) this.managers = managers;
      });
  }

  protected closeManagersPicker(): void {
    this.isManagersPickerOpen = false;
  }

  protected get filteredManagers(): ChatManager[] {
    const searchTerm = this.managerSearchTerm.trim().toLocaleLowerCase();
    if (!searchTerm) return this.managers;

    return this.managers.filter((manager) => manager.name.toLocaleLowerCase().includes(searchTerm));
  }

  protected assignChatManager(managerId: string): void {
    if (this.isAssigningManager || !this.chat?.id) return;

    this.closeManagersPicker();
    this.isAssigningManager = true;
    this.chatService
      .updateChat(this.chat.id, { assigned_user_id: managerId })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isAssigningManager = false)),
      )
      .subscribe({
        // The chat may become inaccessible after transferring it to another manager (chat distribution)
        error: () => this.chatService.reloadChatList(),
      });
  }

  protected readonly isToday = isToday;
  protected readonly isYesterday = isYesterday;
  protected readonly CHAT_EXTERNAL_TYPE = CHAT_EXTERNAL_TYPE;
  protected readonly isCurrentWeek = isCurrentWeek;
  protected readonly MESSAGES_INFO_POSITION = MESSAGES_INFO_POSITION;
  protected readonly SEND_ACTION_TYPE = SEND_ACTION_TYPE;
  protected readonly CHAT_SUB_TYPES = CHAT_SUB_TYPES;
  protected readonly MESSAGE_TYPES = MESSAGE_TYPES;
  protected readonly UPLOAD_FILE_ICONS = UPLOAD_FILE_ICONS;
  protected readonly ATTACHMENTS_TYPES = ATTACHMENTS_TYPES;
  protected readonly OLD_RIA_CHAT_TYPES = OLD_RIA_CHAT_TYPES;
  protected readonly VOICE_MESSAGE_CHAT_TYPES = VOICE_MESSAGE_CHAT_TYPES;
  protected readonly entityIcons = entityIcons;
}
